import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useLanguage, LangToggle, categoryIcons } from '../context/LanguageContext'

function getDaysLeft(expiryDate) {
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
}

function getProgress(purchaseDate, expiryDate) {
  const start = new Date(purchaseDate).getTime()
  const end   = new Date(expiryDate).getTime()
  const now   = Date.now()
  return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))
}

export default function WarrantyDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, translateCategory } = useLanguage()
  const [warranty, setWarranty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    supabase.from('warranties').select('*').eq('id', id).single().then(({ data }) => {
      setWarranty(data); setLoading(false)
    })
  }, [id])

  const handleDelete = async () => {
    if (warranty.image_url) {
      const path = warranty.image_url.split('/Warranties/')[1]
      if (path) await supabase.storage.from('Warranties').remove([path])
    }
    await supabase.from('warranties').delete().eq('id', id)
    navigate('/dashboard')
  }

  if (loading)   return <div className="loading" style={{ marginTop: '40vh' }}>{t.loading}</div>
  if (!warranty) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem' }}>⚠️</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 600, color: '#fff' }}>{t.warrantyNotFound}</div>
      <button onClick={() => navigate('/dashboard')} style={{ marginTop: '1rem', padding: '0.75rem 2rem', background: '#E8610A', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
        {t.dir === 'rtl' ? '\u0627\u0644\u0639\u0648\u062f\u0629 \u0644\u0644\u0631\u0626\u064a\u0633\u064a\u0629' : 'Back to Dashboard'}
      </button>
    </div>
  )

  const days     = getDaysLeft(warranty.expiry_date)
  const progress = getProgress(warranty.purchase_date, warranty.expiry_date)
  const icon     = categoryIcons[warranty.category] || '📦'

  let daysLabel = ''
  let daysColor = '#4ADE80'
  if (days < 0)      { daysLabel = t.warrantyExpired;       daysColor = '#ff8080' }
  else if (days === 0) { daysLabel = t.expiresTodayBang;    daysColor = '#FBBF24' }
  else if (days <= 30) { daysLabel = t.daysLeftDetail(days); daysColor = '#FBBF24' }
  else                 { daysLabel = t.daysLeftDetail(days); daysColor = '#4ADE80' }

  const locale = t.dir === 'ltr' ? 'en-US' : 'ar-SA'

  return (
    <div className="app-layout">
      <div className="topbar">
        <button className="topbar-btn" onClick={() => navigate('/dashboard')} aria-label="Back">{t.dir === 'rtl' ? '→' : '←'}</button>
        <div className="topbar-logo">{t.warrantyDetails}</div>
        <LangToggle />
      </div>

      <div className="main-content">
        <div className="detail-hero">
          <div className="detail-category">{icon} {translateCategory(warranty.category)}</div>
          <div className="detail-name">{warranty.product_name}</div>
          {warranty.brand && <div className="detail-brand">{warranty.brand}</div>}
          <div style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '14px', color: daysColor }}>{daysLabel}</div>
          <div className="detail-progress-bg">
            <div className="detail-progress-fill" style={{ width: `${progress}%`, background: days < 0 ? '#ff8080' : days <= 30 ? '#FBBF24' : '#fff' }} />
          </div>
          <div className="detail-dates">
            <span>{new Date(warranty.purchase_date).toLocaleDateString(locale)}</span>
            <span>{new Date(warranty.expiry_date).toLocaleDateString(locale)}</span>
          </div>
        </div>

        <div className="detail-info-grid">
          <div className="info-card">
            <div className="info-card-label">{t.purchaseDateLabel}</div>
            <div className="info-card-value">{new Date(warranty.purchase_date).toLocaleDateString(locale)}</div>
          </div>
          <div className="info-card">
            <div className="info-card-label">{t.expiryDateLabel}</div>
            <div className="info-card-value">{new Date(warranty.expiry_date).toLocaleDateString(locale)}</div>
          </div>
          {warranty.store && (
            <div className="info-card">
              <div className="info-card-label">{t.storeLabel}</div>
              <div className="info-card-value">{warranty.store}</div>
            </div>
          )}
          {warranty.serial_number && (
            <div className="info-card">
              <div className="info-card-label">{t.serialLabel}</div>
              <div className="info-card-value" style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{warranty.serial_number}</div>
            </div>
          )}
        </div>

        {warranty.notes && (
          <div className="info-card" style={{ marginBottom: '16px' }}>
            <div className="info-card-label">{t.notesLabel}</div>
            <div className="info-card-value" style={{ fontSize: '0.9rem', fontWeight: 400, lineHeight: 1.6 }}>{warranty.notes}</div>
          </div>
        )}

        {warranty.image_url && (
          <div className="info-card" style={{ marginBottom: '16px' }}>
            <div className="info-card-label">{t.warrantyDocLabel}</div>
            <a href={warranty.image_url} target="_blank" rel="noopener noreferrer" className="pdf-view-btn">
              <span>📄</span><span>{t.viewPdf}</span>
              <span style={{ marginRight: 'auto', fontSize: '0.75rem', opacity: 0.7 }}>↗</span>
            </a>
          </div>
        )}

        <button className="btn-full" onClick={() => navigate(`/warranty/${id}/edit`)} style={{ marginBottom: 12 }}>
          {t.editWarranty || (t.dir === 'rtl' ? 'تعديل الضمان' : 'Edit Warranty')}
        </button>
        <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>{t.deleteWarranty}</button>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">🗑️</div>
            <div className="modal-title">{t.deleteConfirm}</div>
            <div className="modal-sub">{t.deleteConfirmSub || ''}</div>
            <div className="modal-actions">
              <button className="btn-outline" onClick={() => setShowDeleteModal(false)}>{t.cancel}</button>
              <button className="delete-btn" style={{ flex: 1 }} onClick={handleDelete}>{t.deleteWarranty}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
